import { Box, Button, Flex, Heading, Stack, Text } from '@chakra-ui/react';

import { useInterviewAgent } from '@/store/InterviewAgent';
import { useState } from 'react';
import { ScreeningStatus } from '@/types/enums';
import { fetchAPI } from '@/api/fetch';
import { apiStartScreening } from '@/api/services';

export default function StartInterviewPage() {
  const { accessToken, screening, nextStep, isCounterRunning, startCounter } = useInterviewAgent();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleStartInterview = async () => {
    if (!accessToken) {
      setError('Something went wrong. Please try again.');
      return;
    }

    if (!screening) {
      setError('Something went wrong. Please try again.');
      return;
    }
    if (screening.status != ScreeningStatus.InProgress) {
      try {
        setIsLoading(true);
        const response = await fetchAPI<void>({
          request: () => apiStartScreening(accessToken)
        });

        if (!response.success) {
          console.error('Error starting interview:', response.message);
          setError('Unable to start interview. Please try again.');
        }
      } catch (error) {
        console.error('Error starting interview:', error);
        setError('Unable to start interview. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    if (!isCounterRunning) {
      startCounter();
    }
    nextStep();
  };

  return (
    <Flex
      flexDir="column"
      flexGrow={1}
      maxW="400px"
      mx="auto"
      justify="center"
      align="center"
      minH="calc(100vh - 240px)"
    >
      <Box w="100%">
        {/* Header */}
        <Heading size="lg" color="neutrals.900" fontSize="36px" mb="36px">
          Ready to Start?
        </Heading>

        <Text fontSize="16px" color="neutrals.700" mb="16px" lineHeight="1.6">
          Your answers will be saved automatically as you go, so you won't lose any progress.
        </Text>
        <Text fontSize="16px" color="neutrals.700" mb="16px" lineHeight="1.6">
          Once the interview begins, you must complete it within the session time limit. Exiting or
          refreshing the page will end your session, and you will not be able to resume.
        </Text>
        <Text fontSize="16px" color="neutrals.700" mb="16px" lineHeight="1.6">
          During the interview, please be aware that after clicking "Next," you won't be able to go
          back to modify your previous answers.
        </Text>
        <Text fontSize="16px" color="neutrals.700" mb="40px" lineHeight="1.6">
          If you have any questions, please contact our support team at Support@PolarisJobs.com.
        </Text>

        {error && (
          <Text fontSize="14px" color="red.500" mb="20px">
            {error}
          </Text>
        )}

        {/* Action Button */}
        <Stack justify="center" align="center">
          <Button
            w="400px"
            bg="primary.900"
            color="white"
            loading={isLoading}
            onClick={handleStartInterview}
            px="32px"
            py="16px"
            fontSize="16px"
            fontWeight="medium"
            borderRadius="8px"
            _hover={{
              bg: 'primary.700'
            }}
            transition="all 0.2s"
          >
            Start Interview
          </Button>
        </Stack>
      </Box>
    </Flex>
  );
}
